import { useState } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { useCreateWorkspaceInvitation } from "@/hooks/useTeam";
import { useWorkspaceRoles } from "@/hooks/useWorkspaceRoles";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Shield, Check } from "lucide-react";

interface InviteMemberWizardProps {
  /** workspaceId used for the invitation API */
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMemberWizard({ workspaceId, open, onOpenChange }: InviteMemberWizardProps) {
  const [step, setStep] = useState(1);
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [roleName, setRoleName] = useState("");
  const [successCount, setSuccessCount] = useState(0);

  const { data: roles = [] } = useWorkspaceRoles();
  const createInvitation = useCreateWorkspaceInvitation(workspaceId);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const parseEmails = (input: string): string[] =>
    input
      .split(/[,\n]/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => EMAIL_RE.test(e));

  const handleNext = async () => {
    if (step === 1) {
      const parsedEmails = parseEmails(emailInput);
      if (parsedEmails.length === 0) {
        showError("Please enter at least one valid email address.", "Invalid email");
        return;
      }
      setEmails(parsedEmails);
      setStep(2);
    } else if (step === 2) {
      const selectedRole = roleName || roles?.[0]?.name || "";

      if (!selectedRole) {
        showError("Please create roles first in the Roles tab.", "No roles available");
        return;
      }

      setStep(3);

      let successfulSends = 0;
      for (const email of emails) {
        try {
          await createInvitation.mutateAsync({ email, role: selectedRole });
          successfulSends++;
        } catch {
          // individual send failure is tracked via successfulSends count
        }
      }

      setSuccessCount(successfulSends);

      if (successfulSends > 0) {
        showSuccess(
          `${successfulSends} invitation${successfulSends > 1 ? "s" : ""} sent successfully`,
          "Invitations sent!"
        );
      } else {
        showError("Failed to send invitations.", "Error");
        setStep(2);
      }
    }
  };

  const handleClose = () => {
    setStep(1);
    setEmailInput("");
    setEmails([]);
    setRoleName("");
    setSuccessCount(0);
    onOpenChange(false);
  };

  const isLoading = createInvitation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>Step {step} of 3</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold mb-2">Enter Email Addresses</h3>
              <p className="text-sm text-muted-foreground">
                Enter one or multiple email addresses
              </p>
            </div>
            <div>
              <Label>Email Addresses *</Label>
              <Textarea
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder={"colleague@example.com, another@example.com\nOr one per line"}
                rows={4}
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                Separate multiple emails with commas or new lines
              </p>
            </div>
            <Button onClick={handleNext} className="w-full">
              Next
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mx-auto">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold mb-2">Select Role (Optional)</h3>
              <p className="text-sm text-muted-foreground">
                What role should{" "}
                {emails.length === 1 ? emails[0] : `these ${emails.length} members`} have? You can
                change this later.
              </p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={roleName} onValueChange={setRoleName}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role (default: first role)" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role._id} value={role.name}>
                      <div>
                        <div className="font-medium">{role.displayName}</div>
                        {role.description && (
                          <div className="text-xs text-muted-foreground">{role.description}</div>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                If not selected, the first available role will be assigned
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button onClick={handleNext} disabled={isLoading} className="flex-1">
                {isLoading ? "Sending..." : "Send Invitation"}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold mb-2">
                {successCount === emails.length ? "All Invitations Sent!" : "Invitations Sent"}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Successfully sent {successCount} of {emails.length} invitation
                {emails.length > 1 ? "s" : ""}.
              </p>
              {emails.length <= 5 && (
                <div className="bg-muted p-3 rounded-lg text-left space-y-1">
                  {emails.map((email, i) => (
                    <div key={i} className="text-sm">
                      ✓ {email}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
